import { Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { getEncumbranceInstitutionName } from '@core/util/encumbrance';
import {
	getMortgageLenderName,
	getMortgageLenders,
	MortgagePriorityWord,
} from '@core/util/mortgage';
import { capitalize, joinNames } from '@core/util/string';
import { Encumbrance, EncumbranceType } from '@models/encumbrance';
import {
	MatterMortgagePriority,
	MatterMortgageType,
	MatterMortgageWithAssociations,
	TransactionType,
} from '@models/mortgage';
import { LenderWithAssociations } from '@models/lender';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { MatterDispatch } from '@state/matter';
import { MatterEncumbrancesState } from '@state/matter/encumbrances';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { MatterMortgagesState } from '@state/matter/matter_mortgages';
import { combineLatest, Observable } from 'rxjs';
import {
	DropdownOption,
	DropdownResult,
} from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';

interface PriorityVm {
	id: string;
	name: string;
	priorityDescription: string;
	allowPriorityChange: boolean;
	new: boolean;
	refNumber: string;
}
interface MortgagePriorityVM extends PriorityVm {
	type: MatterMortgageType;
	priority: number;
}

interface EncumbrancePriorityVM extends PriorityVm {
	type: EncumbranceType;
	priorityAfterClosing: number;
}

const PriorityOptions: DropdownOption<MatterMortgagePriority>[] = [
	{ name: 'First', value: MatterMortgagePriority.PRIORITY_1 },
	{ name: 'Second', value: MatterMortgagePriority.PRIORITY_2 },
	{ name: 'Third', value: MatterMortgagePriority.PRIORITY_3 },
	{ name: 'Fourth', value: MatterMortgagePriority.PRIORITY_4 },
	{ name: 'Fifth', value: MatterMortgagePriority.PRIORITY_5 },
];

@UntilDestroy()
@Component({
	selector: 'app-change-priority',
	templateUrl: './change-priority.component.html',
	standalone: false,
})
export class ChangePriorityComponent implements OnInit {
	public mortgages: MortgagePriorityVM[] = [];
	public encumbrances: EncumbrancePriorityVM[] = [];
	public priorityOptions = PriorityOptions;
	@Select(MatterMortgagesState.getAllMortgagesWithAssociations)
	mortgages$: Observable<MatterMortgageWithAssociations[]>;
	@Select(MatterEncumbrancesState.getRemain) remainEncumbrances$: Observable<
		Encumbrance[]
	>;
	@SelectSnapshot(SelectedMatter.id) matterId: string;
	constructor(
		private matDialog: MatDialogRef<ChangePriorityComponent>,
		private matterDispatch: MatterDispatch,
	) {}

	ngOnInit(): void {
		combineLatest([this.mortgages$, this.remainEncumbrances$])
			.pipe(untilDestroyed(this))
			.subscribe(([_mortgages, _encumbrances]) => {
				this.mortgages = _mortgages
					?.filter(
						(mortgage) =>
							mortgage.transactionType === TransactionType.ARRANGED ||
							mortgage.transactionType === TransactionType.VTB,
					)
					?.map((mortgage) => {
						const lenders = getMortgageLenders(
							mortgage,
						) as LenderWithAssociations[];
						const lenderNames = joinNames(
							lenders.map((lend) => getMortgageLenderName(lend)),
						);

						return {
							id: mortgage.id,
							name: lenderNames,
							priorityDescription: capitalize(
								MortgagePriorityWord[mortgage.priority],
							),
							priority: mortgage.priority,
							type: mortgage.type,
							allowPriorityChange:
								mortgage.type === MatterMortgageType.MANUAL ||
								mortgage.type === MatterMortgageType.CLOSER_SYNC,
							new: true,
							refNumber: mortgage.referenceNumber,
						};
					});

				this.encumbrances = _encumbrances?.map((encumbrance) => ({
					id: encumbrance.id,
					name: getEncumbranceInstitutionName(encumbrance),
					priorityDescription: capitalize(
						MortgagePriorityWord[encumbrance?.priorityAfterClosing],
					),
					priorityAfterClosing: encumbrance?.priorityAfterClosing,
					type: encumbrance.type,
					allowPriorityChange: true,
					new: false,
					refNumber: encumbrance.loanNumber,
				}));
			});
	}

	selectPriority(
		result: DropdownResult<DropdownOption<MatterMortgagePriority>>,
		index: number,
	) {
		this.mortgages[index].priority = result.result.value;
		this.mortgages[index].priorityDescription = capitalize(
			MortgagePriorityWord[result.result.value],
		);
	}

	selectEncumbrancePriority(
		result: DropdownResult<DropdownOption<MatterMortgagePriority>>,
		index: number,
	) {
		this.encumbrances[index].priorityAfterClosing = result.result.value;
		this.encumbrances[index].priorityDescription = capitalize(
			MortgagePriorityWord[result.result.value],
		);
	}

	close() {
		this.matDialog.close();
	}

	save() {
		this.matterDispatch.UpdateMortgagesAndEncumbrancesPriority(
			this.matterId,
			this.mortgages,
			this.encumbrances,
		);
		this.close();
	}
}
